import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, ShieldCheckIcon, BarChart3Icon, MegaphoneIcon, SettingsIcon } from 'lucide-react';
import { acceptAll, rejectAll, setConsent, getConsent, initConsent, OPEN_SETTINGS_EVENT } from '../lib/consent';
const NORDEN_COOKIE_IMG = 'https://cdn.magicpatterns.com/uploads/a7QwrfsRheFwXZaY2yk98u/Norden_die_cookie_eet_nom.png';
export const CookieBanner: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [analytics, setAnalytics] = useState(true);
  const [marketing, setMarketing] = useState(false);
  useEffect(() => {
    initConsent();
    const existing = getConsent();
    if (!existing) {
      const t = window.setTimeout(() => setVisible(true), 600);
      return () => window.clearTimeout(t);
    }
    return undefined;
  }, []);
  useEffect(() => {
    const open = () => {
      const existing = getConsent();
      if (existing) {
        setAnalytics(existing.categories.analytics);
        setMarketing(existing.categories.marketing);
      }
      setShowDetails(true);
      setVisible(true);
    };
    window.addEventListener(OPEN_SETTINGS_EVENT, open);
    return () => window.removeEventListener(OPEN_SETTINGS_EVENT, open);
  }, []);
  const handleAcceptAll = () => {
    acceptAll();
    setVisible(false);
    setShowDetails(false);
  };
  const handleRejectAll = () => {
    rejectAll();
    setVisible(false);
    setShowDetails(false);
  };
  const handleSavePrefs = () => {
    setConsent({
      analytics,
      marketing
    });
    setVisible(false);
    setShowDetails(false);
  };
  return <AnimatePresence data-id="element-250" data-mp="1cjxjjls60t0q">
      {visible && <>
          {/* Backdrop only when details open */}
          {showDetails && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-black/50 z-[9998]" onClick={() => setShowDetails(false)} data-id="element-251" data-mp="3sbblff7a1o6z" />}

          <motion.div initial={{
        y: '100%'
      }} animate={{
        y: 0
      }} exit={{
        y: '100%'
      }} transition={{
        type: 'spring',
        stiffness: 240,
        damping: 30
      }} role="dialog" aria-live="polite" aria-label="Cookie-instellingen" className="fixed bottom-0 left-0 right-0 z-[9999]" data-id="element-252" data-mp="3d81ut15wau6p">
            <div className="relative bg-white border-t-4 border-[#004AC7] shadow-[0_-12px_40px_-12px_rgba(0,0,0,0.25)]" data-id="element-253" data-mp="2ui28un5cpv7i">
              <div className="container mx-auto max-w-[1400px] px-4 md:px-8 py-5 md:py-6" data-id="element-254" data-mp="2pih8c8aj9eso">
                <div className="flex flex-col lg:flex-row lg:items-center gap-5 lg:gap-8" data-id="element-255" data-mp="2lh40xtrsl3jn">
                  {/* Norden — inside banner */}
                  <motion.img initial={{
                y: 10,
                opacity: 0
              }} animate={{
                y: 0,
                opacity: 1
              }} transition={{
                delay: 0.15,
                duration: 0.4
              }} src={NORDEN_COOKIE_IMG} alt="Norden eet een cookie" className="w-20 h-20 md:w-24 md:h-24 lg:w-28 lg:h-28 object-contain flex-shrink-0 self-center lg:self-auto select-none" data-id="element-256" data-mp="2gjm06qnxhumq" />

                  {/* Copy */}
                  <div className="flex-1 min-w-0" data-id="element-257" data-mp="w1b98bw6z0ah">
                    <h3 className="text-lg md:text-xl font-bold text-[#111827] mb-1.5" data-id="element-258" data-mp="w2mdsdxgoc2n">
                      Mag Norden een cookie?
                    </h3>
                    <p className="text-sm text-[#4B5563] leading-relaxed max-w-2xl" data-id="element-259" data-mp="3cw72py5scgk">
                      Wij gebruiken cookies om de site te laten werken, om te
                      leren hoe bezoekers ons vinden en om onze content te
                      verbeteren. Jij bepaalt zelf wat we mogen bewaren.{' '}
                      <a href="/cookiebeleid" className="text-[#004AC7] font-semibold hover:underline" data-id="element-260" data-mp="hcovgem7qgld">
                        Lees meer
                      </a>
                      .
                    </p>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col sm:flex-row lg:flex-row items-stretch sm:items-center gap-2 sm:gap-3 flex-shrink-0" data-id="element-261" data-mp="1raetwi9dcfho">
                    <button onClick={() => setShowDetails(v => !v)} className="inline-flex items-center justify-center gap-2 text-[#1B2B44] font-medium py-2.5 px-4 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors text-sm" data-id="element-262" data-mp="1mc3jo6f5w2yx">
                      <SettingsIcon className="w-4 h-4" data-id="element-263" data-mp="2a7pse0n6f4w2" />
                      Voorkeuren
                    </button>
                    <button onClick={handleRejectAll} className="text-[#1B2B44] font-medium py-2.5 px-4 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors text-sm" data-id="element-264" data-mp="2583fxtf69sob">
                      Alleen noodzakelijk
                    </button>
                    <button onClick={handleAcceptAll} className="bg-[#004AC7] text-white font-semibold py-2.5 px-6 rounded-lg hover:bg-[#003A9E] transition-colors shadow-sm" data-id="element-265" data-mp="2f1ak5ay6f2mt">
                      Alles accepteren
                    </button>
                  </div>
                </div>

                {/* Expandable preferences panel */}
                <AnimatePresence initial={false} data-id="element-266" data-mp="3ckr43pjpi0yu">
                  {showDetails && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3
              }} className="overflow-hidden" data-id="element-267" data-mp="2ykq5yhutt7n2">
                      <div className="pt-6 mt-6 border-t border-gray-100" data-id="element-268" data-mp="2tmy6n2ruqj8u">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5" data-id="element-269" data-mp="2fvji6gf6dpue">
                          <ConsentRow icon={<ShieldCheckIcon className="w-4 h-4" data-id="element-271" data-mp="2jwd81fc6hepx" />} title="Noodzakelijk" description="Vereist om de site te laten werken, zoals het onthouden van je cookie-keuze." checked disabled onChange={() => {}} data-id="element-270" data-mp="2jwve13on77b1" />
                          <ConsentRow icon={<BarChart3Icon className="w-4 h-4" data-id="element-273" data-mp="2awzw8g0l0e21" />} title="Statistieken" description="Anonieme metingen via Google Analytics zodat we de site kunnen verbeteren." checked={analytics} onChange={setAnalytics} data-id="element-272" data-mp="2audtctomymq5" />
                          <ConsentRow icon={<MegaphoneIcon className="w-4 h-4" data-id="element-275" data-mp="7ltxuydn3oj1" />} title="Marketing" description="Meta Pixel zodat we onze advertenties relevanter kunnen maken." checked={marketing} onChange={setMarketing} data-id="element-274" data-mp="7mdbi2w9bqr9" />
                        </div>
                        <div className="flex flex-col sm:flex-row sm:justify-end gap-2" data-id="element-276" data-mp="zczzjj90duaf">
                          <button onClick={() => setShowDetails(false)} className="inline-flex items-center justify-center gap-2 text-[#6B7280] font-medium py-2.5 px-4 rounded-lg hover:bg-gray-50 transition-colors text-sm" data-id="element-277" data-mp="vcph37415beo">
                            <XIcon className="w-4 h-4" data-id="element-278" data-mp="lge4eg0j2jjp" />
                            Sluiten
                          </button>
                          <button onClick={handleSavePrefs} className="bg-[#004AC7] text-white font-semibold py-2.5 px-6 rounded-lg hover:bg-[#003A9E] transition-colors text-sm" data-id="element-279" data-mp="364iq97ukeugn">
                            Voorkeuren opslaan
                          </button>
                        </div>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
};
type ConsentRowProps = {
  icon: React.ReactNode;
  title: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (v: boolean) => void;
};
const ConsentRow: React.FC<ConsentRowProps> = ({
  icon,
  title,
  description,
  checked,
  disabled,
  onChange
}) => {
  return <div className="flex items-start gap-3 p-4 rounded-xl bg-[#F7F8FA] border border-gray-100" data-id="element-280" data-mp="3omwu3962w8p9">
      <div className="w-8 h-8 rounded-md bg-white border border-gray-200 flex items-center justify-center text-[#004AC7] flex-shrink-0" data-id="element-281" data-mp="3tlzuggb45qqw">
        {icon}
      </div>
      <div className="flex-1 min-w-0" data-id="element-282" data-mp="1zaukku9foi34">
        <div className="flex items-center justify-between gap-3 mb-1" data-id="element-283" data-mp="1zbw9a1jdh33b">
          <span className="font-semibold text-sm text-[#111827]" data-id="element-284" data-mp="50mqzx4r843r">{title}</span>
          <button role="switch" aria-checked={checked} aria-label={title} disabled={disabled} onClick={() => !disabled && onChange(!checked)} className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors flex-shrink-0 ${checked ? 'bg-[#004AC7]' : 'bg-gray-300'} ${disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`} data-id="element-285" data-mp="2351wq5usbe">
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-4' : 'translate-x-0.5'}`} data-id="element-286" data-mp="3d9mmjqxjhf77" />
          </button>
        </div>
        <p className="text-xs text-[#6B7280] leading-relaxed" data-id="element-287" data-mp="3r72whcf1nr3p">{description}</p>
      </div>
    </div>;
};